import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, ArrowUpRight, ShieldCheck, Shield, User } from "lucide-react";
import { formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";

/**
 * Phase 1.11-U — Login revizyonları
 *  1) Sol vitrin: video slot (autoplay/muted/loop). VIDEO_ASSET_URL null iken poster (mevcut image) gösterilir.
 *  2) Segmented tabs (Admin | Personel) — sadece UX hint; endpoint aynı.
 *  3) Rol-bazlı redirect: admin/super_admin/manager → "/", artist/staff → "/personel-portal".
 *     Sekme ↔ rol uyuşmazsa friendly toast.
 *  4) hero-fade-right + form-bloom-left ile sinematik geçiş.
 *  5) Brand komponenti gerçek logo asset'i render eder.
 */

const VIDEO_ASSET_URL = "/hero-login.mp4";
const POSTER_IMG = "https://images.pexels.com/photos/2928342/pexels-photo-2928342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

const PILLARS = [
  { k: "01", h: "Operasyon", d: "Randevu, kapora ve onay akışı" },
  { k: "02", h: "Hakediş", d: "Şeffaf komisyon ve ödeme kontrolü" },
  { k: "03", h: "Sanatçı", d: "Portföy, performans ve portal deneyimi" },
];

const TABS = [
  { key: "admin", label: "Admin Girişi", icon: Shield,
    hint: "Yönetici / şube sorumlusu hesabınızla giriş yapın.",
    placeholder: "admin@tattoompark.com",
    home: "/",
    okRoles: ["admin", "super_admin", "manager"],
  },
  { key: "personnel", label: "Personel Girişi", icon: User,
    hint: "Sanatçı veya personel hesabınızla portala erişin.",
    placeholder: "sanatci@tattoompark.com",
    home: "/personel-portal",
    okRoles: ["artist", "staff"],
  },
];

const LS_KEY = "login.lastTab";

// Backend accepts only canonical login_type values: "admin" | "personnel".
// Normalise legacy tab keys / role names that may still live in localStorage
// from earlier builds (Phase 1.11 shipped "personel").
const CANONICAL_TAB_KEYS = new Set(["admin", "personnel"]);
function normaliseTabKey(raw) {
  if (raw === "personel" || raw === "artist" || raw === "staff") return "personnel";
  if (raw === "super_admin") return "admin";
  if (raw && CANONICAL_TAB_KEYS.has(raw)) return raw;
  return "admin";
}

// Merkezi rol → route eşleme (login_type kanonik)
function routeForRole(role) {
  if (role === "artist" || role === "staff") return "/personel-portal";
  return "/";
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [tabKey, setTabKey] = useState(() => {
    try { return normaliseTabKey(localStorage.getItem(LS_KEY)); }
    catch { return "admin"; }
  });
  const tab = TABS.find(t => t.key === tabKey) || TABS[0];

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Reduced-motion + video error → fall back to poster
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const showVideo = !!VIDEO_ASSET_URL && !prefersReducedMotion && !videoFailed;

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!media) return;
    setPrefersReducedMotion(media.matches);
    const onChange = (e) => setPrefersReducedMotion(e.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);

  // Persist tab selection (already canonicalised)
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, tabKey); } catch { /* noop */ }
  }, [tabKey]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await login(email.trim(), password, tab.key);
      const role = data?.role;
      const lt = data?.login_type;
      const dest = routeForRole(role);
      // Backend now enforces login_type match, so client-side mismatch shouldn't happen.
      // Toast success in normal path.
      toast.success("Hoş geldiniz");
      navigate(dest, { replace: true });
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1.05fr_1fr] bg-[#050505] text-white">
      {/* ==== Vitrin (video / hero) ==== */}
      <div className="relative hidden lg:block overflow-hidden hero-fade-right">
        {/* Poster ALWAYS rendered underneath — instant paint, zero layout shift,
            also acts as fallback when video is disabled/failed. */}
        <div
          data-testid="login-hero-poster"
          aria-hidden={showVideo}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${POSTER_IMG})`, filter: "grayscale(1) contrast(1.05) brightness(0.55)" }}
        />
        {showVideo && (
          <video
            ref={videoRef}
            data-testid="login-hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.05) brightness(0.55)" }}
            src={VIDEO_ASSET_URL}
            poster={POSTER_IMG}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
            controls={false}
            aria-hidden="true"
            onError={() => setVideoFailed(true)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/70 to-black/30" />
        <div className="absolute inset-0 premium-glow" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="absolute inset-0 hero-vignette" />

        <div className="relative z-10 h-full flex flex-col justify-between p-14">
          <div>
            <Brand variant="hero" href={false} subtitle="Tattoompark Yönetim · Studio OS" />
          </div>

          <div className="max-w-lg space-y-6">
            <div className="section-divider">
              <span className="text-[10px] uppercase tracking-[0.4em] text-neutral-500">Vitrin</span>
            </div>
            <h1
              data-testid="login-hero-headline"
              className="font-display uppercase text-4xl xl:text-5xl leading-[1.05] tracking-tight"
            >
              Sadece bir panel değil,
              <br />
              <span className="text-brand-red">stüdyonuzun</span> dijital omurgası.
            </h1>
            <p className="text-sm text-neutral-400 leading-relaxed max-w-md">
              Müşteri, randevu, sanatçı, onay ve hakediş süreçlerinizi tek merkezden yönetin.
              Modern dövme stüdyoları için tasarlanmış profesyonel operasyon altyapısı.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-8">
              {PILLARS.map(p => (
                <div key={p.k} className="card-premium rounded-sm p-3">
                  <div className="text-[10px] font-mono text-brand-red mb-1">{p.k}</div>
                  <div className="font-display uppercase text-sm tracking-tight">{p.h}</div>
                  <div className="text-[10px] text-neutral-500 mt-1 leading-relaxed">{p.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-500">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              KVKK Uyumlu · Audit Loglu
            </div>
            <div className="font-mono">v1.0 · 2026</div>
          </div>
        </div>
      </div>

      {/* ==== Form panel ==== */}
      <div className="relative flex items-center justify-center p-6 sm:p-12 premium-backdrop form-bloom-left">
        <div className="w-full max-w-sm relative z-10">
          <div className="lg:hidden mb-10">
            <Brand variant="sidebar" href={false} subtitle="Tattoompark Yönetim" />
          </div>

          {/* Segmented tabs */}
          <div className="mb-8 flex flex-col items-start">
            <div className="segmented-tabs" role="tablist" data-testid="login-tabs">
              {TABS.map(t => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tabKey === t.key}
                    data-testid={`login-tab-${t.key}`}
                    onClick={() => setTabKey(t.key)}
                    className={`segmented-tab ${tabKey === t.key ? "active" : ""}`}
                  >
                    <Icon className="w-3 h-3" strokeWidth={2.2} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-3xl uppercase font-bold tracking-tight leading-none">
              {tab.key === "admin" ? (
                <>Portala <span className="text-brand-red">Erişim</span></>
              ) : (
                <>Sanatçı <span className="text-brand-red">Portalı</span></>
              )}
            </h2>
            <p data-testid="login-tab-hint" className="text-sm text-neutral-500 mt-3 leading-relaxed">
              {tab.hint}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400">E-posta</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600" />
                <Input
                  type="email"
                  data-testid={LOGIN.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={tab.placeholder}
                  autoComplete="email"
                  className="pl-10 h-11 bg-black border-white/10 focus-visible:ring-1 focus-visible:ring-brand-red focus-visible:border-brand-red"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Şifre</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600" />
                <Input
                  type="password"
                  data-testid={LOGIN.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="pl-10 h-11 bg-black border-white/10 focus-visible:ring-1 focus-visible:ring-brand-red focus-visible:border-brand-red"
                  required
                />
              </div>
            </div>

            {err && (
              <div data-testid={LOGIN.errorMessage} className="text-xs text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2 rounded-sm">
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid={LOGIN.submitButton}
              disabled={loading}
              className="w-full h-11 bg-brand-red hover:bg-brand-red-dark text-white uppercase tracking-widest text-xs font-bold rounded-sm btn-glow group"
            >
              {loading ? "Giriş yapılıyor..." : (
                <span className="inline-flex items-center gap-2">
                  Giriş Yap
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </span>
              )}
            </Button>
            <div className="pt-2 text-center">
              <Link
                to="/sifremi-unuttum"
                data-testid="login-forgot-link"
                className="inline-block min-h-[44px] py-3 text-[11px] uppercase tracking-widest text-neutral-500 hover:text-brand-red transition-colors"
              >
                Şifremi Unuttum
              </Link>
            </div>
          </form>

          <div className="mt-10 pt-6 border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-600">
            <span>© Tattoompark 2026</span>
            <span>Studio Operations Platform</span>
          </div>
        </div>
      </div>
    </div>
  );
}
